import {Routes} from '@src/Constant/routes';
import {appNavigate, getAppRoutePrefix} from './navigationUtils';
import {navigate} from 'gatsby';
import {useDispatch} from 'react-redux';
import appSlice from '@src/Store/Slices/AppSlice';
import {IAppHeaderOptions} from '@src/layout/AppLayout/AppHeader/AppHeader';
import pageSlice from '@src/Store/Slices/PageSlice';
import {useAppRouterContext} from '@src/App/Route/AppRouterContext/AppRouterContext';

export interface UseAppNavigationReturn {
  drawer: (to: Routes) => void;
  modal: (to: Routes) => void;
  navigate: typeof appNavigate;
  goBack: (nb?: number) => void;
  setOptions: (options?: IAppHeaderOptions) => void;
  setModalBackground: (options?: boolean | string) => void;
  setParams: (params: Record<string, any>) => void;
}

const useAppNavigation = (): UseAppNavigationReturn => {
  const dispatch = useDispatch();
  const {canGoBack, isModal, isDrawer, backgroundLocation, depth} =
    useAppRouterContext();
  const drawer = (to: Routes) => {
    appNavigate(to, {
      state: {
        type: 'drawer',
      },
    });
  };
  const modal = (to: Routes) => {
    appNavigate(to, {
      state: {
        type: 'modal',
      },
    });
  };

  const setOptions = (options?: IAppHeaderOptions) => {
    dispatch(appSlice.actions.setHeaderOptions(options));
  };
  const setModalBackground = (options?: boolean | string) => {
    dispatch(appSlice.actions.setModalBackground(options));
  };

  // Function to handle going back to home when there's no valid history
  const goBackWithHome = () => {
    // Get the HOME route URL
    const appRoutePrefix = getAppRoutePrefix();
    const homeUrl = [getAppRoutePrefix(), Routes.HOME]
      .map(s => s.replace(/^\/|\/$/g, ''))
      .filter(Boolean)
      .join('/');
    const finalHomeUrl = '/' + homeUrl;

    // Get the current location
    const currentLocation = window.location.href;

    // Replace the current URL in history with HOME
    window.history.replaceState(
      {
        ...window.history.state,
        // Keep any relevant state
        key: new Date().toISOString(),
      },
      '',
      finalHomeUrl,
    );

    // Push the current URL back to history
    window.history.pushState(
      {
        ...window.history.state,
        oldLocation: {
          pathname: finalHomeUrl,
          search: '',
          hash: '',
          state: {},
        },
      },
      '',
      currentLocation,
    );

    // Now go back to the HOME route we just inserted at -1
    return navigate(-1);
  };

  const resetNavigationState = () => {
    dispatch(pageSlice.actions.setParams({}));
    dispatch(appSlice.actions.setModalHeader(null));
    setOptions(undefined);
  };

  const hasSameOriginHistory = (): boolean => {
    if (window.history.length <= 1) return false;
    try {
      const referrer = document.referrer;
      if (!referrer) return false;
      const refUrl = new URL(referrer);
      return refUrl.origin === window.location.origin;
    } catch {
      return false;
    }
  };

  const backTo = (nb?: number) => {
    resetNavigationState();
    const nbr = nb && isNaN(nb) ? 1 : nb;
    return navigate(nbr ? -nbr : -1);
  };

  const goBack = (nb?: number) => {
    if ((canGoBack || nb) && hasSameOriginHistory()) {
      return backTo(nb);
    }

    if (isModal || isDrawer) {
      if (backgroundLocation && depth) {
        return backTo(depth + 1);
      }
    }

    return goBackWithHome();
  };

  return {
    drawer,
    modal,
    navigate: ((...args) => {
      dispatch(pageSlice.actions.setParams({}));
      dispatch(appSlice.actions.setModalHeader(null));
      setOptions(undefined);
      return appNavigate(...args);
    }) as typeof appNavigate,
    goBack,
    setOptions,
    setModalBackground,
    setParams: (params: Record<string, any>) => {
      dispatch(pageSlice.actions.setParams(params));
    },
  };
};

export default useAppNavigation;
